import React, { useState, Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import { GlobalStyle } from './styles';

import api from './services/api/customer';

import PrivateRoute from './Components/PrivateRoute';
import LoadingPage from './Components/LoadingPage';
import useDynamicCSS from './hooks/useDynamicCSS';

import './styles/_reboot.css';
import { useTranslation } from 'react-i18next';

// Lazy load all main routes for code splitting
const MenuPage = React.lazy(() => import('./Pages/MenuPage'));
const LoginPage = React.lazy(() => import('./Pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./Pages/DashboardPage'));
const GlobalSnackbar = React.lazy(() => import('./Components/GlobalSnackbar'));
const GlobalDialog = React.lazy(() => import('./Components/GlobalDialog'));
const CustomerLoginPage = React.lazy(() => import('./Pages/CustomerLoginPage'));
const CustomerCompleteProfile = React.lazy(() =>
    import('./Pages/CustomerCompleteProfile'),
);
const CustomerBookingPage = React.lazy(() =>
    import('./Pages/CustomerBookingPage'),
);
const ConciergePage = React.lazy(() => import('./Pages/ConciergePage'));

const Container = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
    min-height: 100vh;
`;

// Initialize Firebase
const firebaseConfig = {
    apiKey: 'AIzaSyBRYX0L47Rfn35WLw9xd_dGfBYqly9isvk',
    authDomain: 'zmenu-reservas.firebaseapp.com',
    projectId: 'zmenu-reservas',
    storageBucket: 'zmenu-reservas.appspot.com',
    messagingSenderId: '658982559926',
    appId: '1:658982559926:web:97355165d46a2e2b02c617',
    measurementId: 'G-CZBW3EH7YC',
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);

// Create basic theme for Material-UI v5
const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        text: {
            primary: '#000000',
            secondary: '#666666',
        },
        background: {
            default: '#ffffff',
            paper: '#ffffff',
        },
    },
});

const App = () => {
    const { t } = useTranslation();
    const { loaded: cssLoaded } = useDynamicCSS();

    const [customerToken, setCustomerToken] = useState(null);
    const [customerProfile, setCustomerProfile] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [user, setUser] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);
            
            if (firebaseUser) {
                try {
                    const token = await firebaseUser.getIdToken();
                    setCustomerToken(token);
                } catch (error) {
                    console.error('Error getting token:', error);
                }
            } else {
                setCustomerToken(null);
                setCustomerProfile(null);
            }
            
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (customerToken) {
            loadUserProfile();
        }
        // eslint-disable-next-line
    }, [customerToken]);

    const loadUserProfile = async () => {
        try {
            const response = await api.get('/booking/profile', {
                headers: {
                    Authorization: `Bearer ${customerToken}`,
                },
            });

            if (response?.status === 200) {
                setCustomerProfile(response.data);

                localStorage.setItem(
                    'customer_info',
                    JSON.stringify({
                        userToken: customerToken,
                        ...response.data,
                    }),
                );
            }
        } catch (error) {
            const response = error.response;
            
            window.dialog(
                t('dialogs.attention'),
                response?.data?.message ||
                    (response?.status === 401
                        ? t('dialogs.invalid_login')
                        : t('dialogs.gone_wrong')),
                'error',
            );

            localStorage.clear();
            signOut(auth);
        }
    };

    const renderReservasRoutes = () => {
        if (authLoading) {
            return <Route path="/reservas/*" element={<LoadingPage />} />;
        }

        if (!user) {
            return (
                <>
                    <Route path="/reservas/login" element={
                        <Suspense fallback={<LoadingPage />}>
                            <CustomerLoginPage />
                        </Suspense>
                    } />
                    <Route path="/reservas" element={<Navigate to="/reservas/login" replace />} />
                    <Route path="/reservas/perfil/completar" element={<Navigate to="/reservas/login" replace />} />
                </>
            );
        }

        if (!customerProfile) {
            return <Route path="/reservas/*" element={<LoadingPage />} />;
        }

        if (!customerProfile.completed || !customerProfile.email_verified) {
            return (
                <>
                    <Route path="/reservas/perfil/completar" element={
                        <Suspense fallback={<LoadingPage />}>
                            <CustomerCompleteProfile />
                        </Suspense>
                    } />
                    <Route path="/reservas/*" element={<Navigate to="/reservas/perfil/completar" replace />} />
                </>
            );
        }

        return (
            <>
                <Route path="/reservas" element={
                    <Suspense fallback={<LoadingPage />}>
                        <CustomerBookingPage />
                    </Suspense>
                } />
                <Route path="/reservas/login" element={<Navigate to="/reservas" replace />} />
                <Route path="/reservas/perfil/completar" element={<Navigate to="/reservas" replace />} />
            </>
        );
    };

    // Show loading while CSS is being loaded
    if (!cssLoaded) {
        return <LoadingPage />;
    }

    return (
        <ThemeProvider theme={theme}>
            <Container className="App">
            <Suspense fallback={''}>
                <GlobalSnackbar />
                <GlobalDialog />
            </Suspense>

            <GlobalStyle />

            <BrowserRouter>
                <Routes>
                    <Route
                        path="/"
                        element={
                            <Suspense fallback={<LoadingPage />}>
                                <MenuPage />
                            </Suspense>
                        }
                    />

                    <Route path="/admin/login" element={
                        <Suspense fallback={<LoadingPage />}>
                            <LoginPage />
                        </Suspense>
                    } />
                    
                    <Route path="/concierge/login" element={
                        <Suspense fallback={<LoadingPage />}>
                            <LoginPage />
                        </Suspense>
                    } />

                    <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
                    <Route path="/concierge" element={<Navigate to="/concierge/login" replace />} />

                    <Route path="/concierge/*" element={
                        <PrivateRoute>
                            <Suspense fallback={<LoadingPage />}>
                                <ConciergePage />
                            </Suspense>
                        </PrivateRoute>
                    } />

                    <Route path="/admin/*" element={
                        <PrivateRoute>
                            <Suspense fallback={<LoadingPage />}>
                                <DashboardPage />
                            </Suspense>
                        </PrivateRoute>
                    } />

                    {renderReservasRoutes()}
                </Routes>
            </BrowserRouter>
        </Container>
        </ThemeProvider>
    );
};

export default App;
